import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/api/client";

export interface BomOperationAllocationItem {
  id: string;
  bom_item: string;
  routing_operation: string;
  is_active: boolean;
  bom_item_display?: {
    id: string;
    bom_revision_id: string;
    material_revision_name?: string;
    material_revision_sku?: string;
    quantity: number;
    sequence_no: number;
  };
  routing_operation_display?: {
    id: string;
    operation_code: string;
    operation_name: string;
  };
}

export interface CreateAllocationPayload {
  bom_item: string;
  routing_operation: string;
}

export const BOM_ALLOCATIONS_QUERY_KEY = ["v1ProductionBomOperationAllocations"];

export async function fetchBomOperationAllocations(params?: {
  bom_item?: string;
  routing_operation?: string;
}): Promise<BomOperationAllocationItem[]> {
  const res = await axiosInstance.get("/api/v1/production/bom-operation-allocations/", { params });
  return Array.isArray(res.data) ? res.data : (res.data.results ?? []);
}

export async function createBomOperationAllocation(
  payload: CreateAllocationPayload
): Promise<BomOperationAllocationItem> {
  const res = await axiosInstance.post("/api/v1/production/bom-operation-allocations/", payload);
  return res.data;
}

export async function activateBomOperationAllocation(id: string): Promise<BomOperationAllocationItem> {
  const res = await axiosInstance.post(`/api/v1/production/bom-operation-allocations/${id}/activate/`);
  return res.data;
}

export async function deactivateBomOperationAllocation(id: string): Promise<BomOperationAllocationItem> {
  const res = await axiosInstance.post(`/api/v1/production/bom-operation-allocations/${id}/deactivate/`);
  return res.data;
}

// Hooks
export function useBomOperationAllocationsQuery(params?: { bom_item?: string; routing_operation?: string }) {
  return useQuery({
    queryKey: [...BOM_ALLOCATIONS_QUERY_KEY, params],
    queryFn: () => fetchBomOperationAllocations(params),
  });
}

export function useCreateBomOperationAllocationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBomOperationAllocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOM_ALLOCATIONS_QUERY_KEY });
    },
  });
}

export function useActivateBomOperationAllocationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: activateBomOperationAllocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOM_ALLOCATIONS_QUERY_KEY });
    },
  });
}

export function useDeactivateBomOperationAllocationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deactivateBomOperationAllocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOM_ALLOCATIONS_QUERY_KEY });
    },
  });
}
